import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/formatters'
import { COVERAGE_TYPE_LABELS } from '@/models/insuranceCoverage'
import type { QuoteCartQuote } from './quoteCartItems'

const WHOLE_DOLLARS = { minimumFractionDigits: 0, maximumFractionDigits: 0 }

/** One carrier in the cart: who it is, the coverages added from it, and a Remove for all of them. */
export function QuoteCartQuoteRow({ quote, onRemove }: { quote: QuoteCartQuote; onRemove: () => void }) {
  return (
    <li className="border-ink-100 flex flex-col gap-3 border-b pb-4 last:border-b-0 last:pb-0">
      <div className="flex items-center gap-3">
        <img
          src={quote.iconUrl}
          alt={quote.iconAlt}
          className="border-ink-50 size-9 shrink-0 rounded-full border object-contain"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-foreground truncate font-semibold">{quote.carrierName}</span>
          {quote.carrierProgram && <span className="text-ink-400 text-xs">{quote.carrierProgram}</span>}
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={onRemove}
          aria-label={`Remove ${quote.carrierName} from cart`}
          className="text-red-700 hover:bg-red-50 hover:text-red-800"
        >
          Remove
        </Button>
      </div>
      <dl className="flex flex-col gap-1.5 pl-12">
        {quote.coverages.map((coverage) => (
          <div key={coverage.type} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-ink-500">{COVERAGE_TYPE_LABELS[coverage.type]}</dt>
            {/* Left empty rather than dropped when priced as one total: a dl group needs its dd. */}
            <dd className="text-foreground font-semibold">
              {coverage.premium !== null
                ? formatCurrency(coverage.premium, WHOLE_DOLLARS)
                : !quote.pricedAsOneTotal && 'Included'}
            </dd>
          </div>
        ))}
        {quote.oneTimeFees.map((fee) => (
          <div key={fee.id} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-ink-500">{fee.label}</dt>
            <dd className="text-foreground font-semibold">{formatCurrency(fee.amount, WHOLE_DOLLARS)}</dd>
          </div>
        ))}
      </dl>
    </li>
  )
}
